import {
  AccountOnboarding,
  ONBOARDING_BUSINESS_TYPE,
  ONBOARDING_PLATFORM,
} from '@adtrackify/at-tracking-event-types';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { applyOnboardingToAccountsCache } from '@/features/setup/helpers/onboarding-cache-update';
import {
  classifyApiError,
} from '@/lib/helpers/api-errors/api-error';
import {
  ApiErrorClass,
} from '@/lib/types/api-error-enums';
import { toastApiError } from '@/components/error-handling/toast-api-error';
import { useAccountState } from '@/contexts/account/account-context';
import { AccountsApiService } from '@/services/internal-api/accounts/accounts-api-service';
import type { UpdateAccountOnboardingPayload } from '@/services/internal-api/accounts/types/accounts-api-service-types';
import { useOnboardingFlowStore } from '@/features/setup/stores/use-onboarding-flow-store';
import type { DataSourceConnections, DestinationConnections } from '@/features/setup/types/use-onboarding-flow-store-types';
import { OnboardingBusinessType, OnboardingPlatform } from '@/features/setup/types/use-onboarding-flow-store-enums';

const SYNC_DEBOUNCE_MS = 500;

interface OnboardingSyncSnapshot {
  accountId: string;
  businessType: OnboardingBusinessType | null;
  platform: OnboardingPlatform | null;
  dataSourceConnected: boolean;
  trackingInstalled: boolean;
  eventsConfigured: boolean;
  firstEventReceived: boolean;
  costsConfigured: boolean;
  dataSources: DataSourceConnections;
  destinations: DestinationConnections;
  dismissed: boolean;
  requiredCompletedAt: string | null;
  recommendedDismissed: boolean;
  timezoneConfirmed: boolean;
}

function snapshotEquals(a: OnboardingSyncSnapshot, b: OnboardingSyncSnapshot): boolean {
  return (
    a.accountId === b.accountId &&
    a.businessType === b.businessType &&
    a.platform === b.platform &&
    a.dataSourceConnected === b.dataSourceConnected &&
    a.trackingInstalled === b.trackingInstalled &&
    a.eventsConfigured === b.eventsConfigured &&
    a.firstEventReceived === b.firstEventReceived &&
    a.costsConfigured === b.costsConfigured &&
    a.dismissed === b.dismissed &&
    a.requiredCompletedAt === b.requiredCompletedAt &&
    a.recommendedDismissed === b.recommendedDismissed &&
    a.timezoneConfirmed === b.timezoneConfirmed &&
    a.dataSources.facebookAds === b.dataSources.facebookAds &&
    a.dataSources.googleAds === b.dataSources.googleAds &&
    a.dataSources.tiktokAds === b.dataSources.tiktokAds &&
    a.dataSources.stripe === b.dataSources.stripe &&
    a.destinations.metaCapi === b.destinations.metaCapi &&
    a.destinations.googleAdsCapi === b.destinations.googleAdsCapi &&
    a.destinations.tiktokEventsApi === b.destinations.tiktokEventsApi &&
    a.destinations.klaviyo === b.destinations.klaviyo
  );
}

function buildPayload(s: OnboardingSyncSnapshot): UpdateAccountOnboardingPayload {
  const onboarding: Partial<AccountOnboarding> = {
    dataSourceConnected: s.dataSourceConnected,
    trackingInstalled: s.trackingInstalled,
    eventsConfigured: s.eventsConfigured,
    firstEventReceived: s.firstEventReceived,
    dataSources: s.dataSources,
    destinations: s.destinations,
    dismissed: s.dismissed,
    recommendedDismissed: s.recommendedDismissed,
    timezoneConfirmed: s.timezoneConfirmed,
    // costsConfigured is not yet in the AccountOnboarding type but is accepted by the backend
    costsConfigured: s.costsConfigured,
  } as Partial<AccountOnboarding>;
  if (s.requiredCompletedAt) {
    onboarding.requiredCompletedAt = s.requiredCompletedAt;
  }

  const payload: UpdateAccountOnboardingPayload = { onboarding };
  if (s.businessType) {
    // Store enum values match the backend enum — safe to narrow.
    payload.onboardingBusinessType = s.businessType as unknown as ONBOARDING_BUSINESS_TYPE;
  }
  if (s.platform) {
    payload.onboardingPlatform = s.platform as unknown as ONBOARDING_PLATFORM;
  }
  return payload;
}

function snapshotFromBackend(
  accountId: string,
  account: { onboardingBusinessType?: ONBOARDING_BUSINESS_TYPE; onboardingPlatform?: ONBOARDING_PLATFORM; onboarding?: AccountOnboarding },
  fallback: OnboardingSyncSnapshot
): OnboardingSyncSnapshot {
  const o = account.onboarding ?? {};
  return {
    accountId,
    businessType:
      (account.onboardingBusinessType as unknown as OnboardingBusinessType | undefined) ??
      fallback.businessType,
    platform:
      (account.onboardingPlatform as unknown as OnboardingPlatform | undefined) ??
      fallback.platform,
    dataSourceConnected: o.dataSourceConnected ?? fallback.dataSourceConnected,
    trackingInstalled: o.trackingInstalled ?? fallback.trackingInstalled,
    eventsConfigured: o.eventsConfigured ?? fallback.eventsConfigured,
    firstEventReceived: o.firstEventReceived ?? fallback.firstEventReceived,
    costsConfigured: (o as AccountOnboarding & { costsConfigured?: boolean }).costsConfigured ?? fallback.costsConfigured,
    dataSources: {
      facebookAds: o.dataSources?.facebookAds ?? fallback.dataSources.facebookAds,
      googleAds: o.dataSources?.googleAds ?? fallback.dataSources.googleAds,
      tiktokAds: o.dataSources?.tiktokAds ?? fallback.dataSources.tiktokAds,
      stripe: o.dataSources?.stripe ?? fallback.dataSources.stripe,
    },
    destinations: {
      metaCapi: o.destinations?.metaCapi ?? fallback.destinations.metaCapi,
      googleAdsCapi: o.destinations?.googleAdsCapi ?? fallback.destinations.googleAdsCapi,
      tiktokEventsApi: o.destinations?.tiktokEventsApi ?? fallback.destinations.tiktokEventsApi,
      klaviyo: o.destinations?.klaviyo ?? fallback.destinations.klaviyo,
    },
    dismissed: o.dismissed ?? fallback.dismissed,
    requiredCompletedAt: o.requiredCompletedAt ?? fallback.requiredCompletedAt,
    recommendedDismissed: o.recommendedDismissed ?? fallback.recommendedDismissed,
    timezoneConfirmed: o.timezoneConfirmed ?? fallback.timezoneConfirmed,
  };
}

export function useOnboardingBackendSync(): void {
  const queryClient = useQueryClient();
  const lastBackendSnapshotRef = useRef<OnboardingSyncSnapshot | null>(null);
  const hasHydratedForAccount = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSnapshotRef = useRef<OnboardingSyncSnapshot | null>(null);
  const inFlightRef = useRef(false);
  const flushPendingRef = useRef<((retryCount?: number) => Promise<void>) | null>(null);

  const { accountId, currentAccount } = useAccountState();

  const state = useOnboardingFlowStore(
    useShallow((s) => ({
      businessType: s.businessType,
      platform: s.platform,
      dataSourceConnected: s.dataSourceConnected,
      trackingInstalled: s.trackingInstalled,
      eventsConfigured: s.eventsConfigured,
      firstEventReceived: s.firstEventReceived,
      costsConfigured: s.costsConfigured,
      dataSources: s.dataSources,
      destinations: s.destinations,
      dismissed: s.dismissed,
      requiredCompletedAt: s.requiredCompletedAt,
      recommendedDismissed: s.recommendedDismissed,
      timezoneConfirmed: s.timezoneConfirmed,
    }))
  );

  useEffect(() => {
    if (!currentAccount || !accountId) return;
    if (hasHydratedForAccount.current === accountId) return;

    const hasBackendOnboarding =
      currentAccount.onboarding !== undefined &&
      currentAccount.onboarding !== null &&
      Object.keys(currentAccount.onboarding).length > 0;

    const cleanFallback: OnboardingSyncSnapshot = {
      accountId,
      businessType: null,
      platform: null,
      dataSourceConnected: false,
      trackingInstalled: false,
      eventsConfigured: false,
      firstEventReceived: false,
      costsConfigured: false,
      dataSources: { facebookAds: false, googleAds: false, tiktokAds: false, stripe: false },
      destinations: { metaCapi: false, googleAdsCapi: false, tiktokEventsApi: false, klaviyo: false },
      dismissed: false,
      requiredCompletedAt: null,
      recommendedDismissed: false,
      timezoneConfirmed: false,
    };

    const storeState = useOnboardingFlowStore.getState();
    const storeFallback: OnboardingSyncSnapshot = {
      accountId,
      businessType: storeState.businessType,
      platform: storeState.platform,
      dataSourceConnected: storeState.dataSourceConnected,
      trackingInstalled: storeState.trackingInstalled,
      eventsConfigured: storeState.eventsConfigured,
      firstEventReceived: storeState.firstEventReceived,
      costsConfigured: storeState.costsConfigured,
      dataSources: storeState.dataSources,
      destinations: storeState.destinations,
      dismissed: storeState.dismissed,
      requiredCompletedAt: storeState.requiredCompletedAt,
      recommendedDismissed: storeState.recommendedDismissed,
      timezoneConfirmed: storeState.timezoneConfirmed,
    };

    const fallback = hasBackendOnboarding ? storeFallback : cleanFallback;

    const backendSnapshot = snapshotFromBackend(accountId, currentAccount, fallback);

    useOnboardingFlowStore.setState({
      businessType: backendSnapshot.businessType,
      platform: backendSnapshot.platform,
      dataSourceConnected: backendSnapshot.dataSourceConnected,
      trackingInstalled: backendSnapshot.trackingInstalled,
      eventsConfigured: backendSnapshot.eventsConfigured,
      firstEventReceived: backendSnapshot.firstEventReceived,
      costsConfigured: backendSnapshot.costsConfigured,
      dataSources: backendSnapshot.dataSources,
      destinations: backendSnapshot.destinations,
      dismissed: backendSnapshot.dismissed,
      requiredCompletedAt: backendSnapshot.requiredCompletedAt,
      recommendedDismissed: backendSnapshot.recommendedDismissed,
      timezoneConfirmed: backendSnapshot.timezoneConfirmed,
    });

    lastBackendSnapshotRef.current = backendSnapshot;
    hasHydratedForAccount.current = accountId;
  }, [currentAccount, accountId]);

  // Debounced sync; dismiss flushes immediately (user may navigate within 500ms).
  useEffect(() => {
    if (!accountId) return;
    if (hasHydratedForAccount.current !== accountId) return;

    const currentStoreState = useOnboardingFlowStore.getState();
    const snapshot: OnboardingSyncSnapshot = {
      accountId,
      businessType: currentStoreState.businessType,
      platform: currentStoreState.platform,
      dataSourceConnected: currentStoreState.dataSourceConnected,
      trackingInstalled: currentStoreState.trackingInstalled,
      eventsConfigured: currentStoreState.eventsConfigured,
      firstEventReceived: currentStoreState.firstEventReceived,
      costsConfigured: currentStoreState.costsConfigured,
      dataSources: currentStoreState.dataSources,
      destinations: currentStoreState.destinations,
      dismissed: currentStoreState.dismissed,
      requiredCompletedAt: currentStoreState.requiredCompletedAt,
      recommendedDismissed: currentStoreState.recommendedDismissed,
      timezoneConfirmed: currentStoreState.timezoneConfirmed,
    };

    if (
      lastBackendSnapshotRef.current &&
      snapshotEquals(lastBackendSnapshotRef.current, snapshot)
    ) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      pendingSnapshotRef.current = null;
      return;
    }

    pendingSnapshotRef.current = snapshot;

    const justDismissed =
      currentStoreState.dismissed && !lastBackendSnapshotRef.current?.dismissed;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (justDismissed) {
      void flushPending();
    } else {
      timeoutRef.current = setTimeout(() => void flushPending(), SYNC_DEBOUNCE_MS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [accountId, state]);

  useEffect(() => {
    flushPendingRef.current = flushPending;
  });

  // Flush on unmount to prevent dropped writes.
  useEffect(() => {
    return () => {
      void flushPendingRef.current?.();
    };
  }, []);

  async function flushPending(retryCount = 0): Promise<void> {
    if (inFlightRef.current) {
      timeoutRef.current = setTimeout(() => void flushPending(retryCount), SYNC_DEBOUNCE_MS);
      return;
    }
    const pending = pendingSnapshotRef.current;
    if (!pending) return;
    pendingSnapshotRef.current = null;

    inFlightRef.current = true;
    try {
      const response = await AccountsApiService.updateAccountOnboarding(pending.accountId, buildPayload(pending));
      lastBackendSnapshotRef.current = pending;

      applyOnboardingToAccountsCache(
        queryClient,
        pending.accountId,
        response.data.onboarding
      );
    } catch (error) {
      console.error('Failed to sync onboarding state:', error);

      if (classifyApiError(error) === ApiErrorClass.PERMISSION) {
        toastApiError(error, 'Failed to save onboarding progress');
        return;
      }

      // Retry dismissed state up to 2 times — this is the most critical field to persist
      if (pending.dismissed && retryCount < 2) {
        pendingSnapshotRef.current = pending;
        timeoutRef.current = setTimeout(() => void flushPending(retryCount + 1), SYNC_DEBOUNCE_MS * 2);
      }
    } finally {
      inFlightRef.current = false;
      if (pendingSnapshotRef.current) {
        timeoutRef.current = setTimeout(() => void flushPending(0), SYNC_DEBOUNCE_MS);
      }
    }
  }
}
